import type {
  CardEntryDetails,
  CardEntryEventName,
  CheckoutEventError,
  CheckoutLifecycleEvent,
  CheckoutLifecyclePhase,
  CheckoutPaymentStatus,
  EventProduct,
  EventPromoCode,
  OrderAwardingEvent,
  PaymentIntentEvent,
  PaymentIntentStage,
  CardEntryUiInteractionsEvent,
  PaymentMethodSelectedUiInteractionsEvent,
} from '@appcharge/frontend-events';
import { FrontendCheckoutEventType } from '@/constants/frontendEvents';
import { IframeMessage } from '@/constants/iframeMessages';
import type { EventData, PromoCodeData } from '@/services/iframeMessenger';

/**
 * Caller-supplied checkout event bodies — exact package DTO props + `event_type`.
 * Base `FrontendEvent` identity/device fields are stamped by the SDK enricher.
 */
export type CheckoutLifecycleEventInput = Omit<
  CheckoutLifecycleEvent,
  keyof import('@appcharge/frontend-events').FrontendEvent
> & {
  event_type: typeof FrontendCheckoutEventType.CHECKOUT_LIFECYCLE;
  event_timestamp: string;
  session_id: string;
  country_code: string;
};

export type OrderAwardingEventInput = Omit<
  OrderAwardingEvent,
  keyof import('@appcharge/frontend-events').FrontendEvent
> & {
  event_type: typeof FrontendCheckoutEventType.ORDER_AWARDING;
  event_timestamp: string;
  session_id: string;
  country_code: string;
};

export type PaymentIntentEventInput = Omit<
  PaymentIntentEvent,
  keyof import('@appcharge/frontend-events').FrontendEvent
> & {
  event_type: typeof FrontendCheckoutEventType.PAYMENT_INTENT;
  event_timestamp: string;
  session_id: string;
  country_code: string;
};

export type CardEntryUiInteractionsEventInput = Omit<
  CardEntryUiInteractionsEvent,
  keyof import('@appcharge/frontend-events').FrontendEvent
> & {
  event_type: typeof FrontendCheckoutEventType.CHECKOUT_UI_INTERACTIONS;
  event_timestamp: string;
  session_id: string;
  country_code: string;
};

export type PaymentMethodSelectedUiInteractionsEventInput = Omit<
  PaymentMethodSelectedUiInteractionsEvent,
  keyof import('@appcharge/frontend-events').FrontendEvent
> & {
  event_type: typeof FrontendCheckoutEventType.CHECKOUT_UI_INTERACTIONS;
  event_timestamp: string;
  session_id: string;
  country_code: string;
};

export type CheckoutEventInput =
  | CheckoutLifecycleEventInput
  | OrderAwardingEventInput
  | PaymentIntentEventInput
  | CardEntryUiInteractionsEventInput
  | PaymentMethodSelectedUiInteractionsEventInput;

/** Optional checkout-context tags not carried on {@link EventData} (see `mixpanelProperties.ts`). */
export interface CheckoutUiInteractionsContextTags {
  checkoutPresentation?: string;
  templateType?: string;
}

/** Checkout-specific fields shared by every package checkout DTO. */
type CheckoutEventCoreFields = {
  order_id: string;
  event_timestamp: string;
  bundle_id: string;
  bundle_name: string;
  bundle_sku: string;
  products: EventProduct[];
  session_id: string;
  total_sum: number;
  total_sum_currency: string;
  country_code: string;
  player_id: string;
  promo_code: EventPromoCode | null;
};

/**
 * Maps an iframe SDK message to the matching `@appcharge/frontend-events` DTO shape.
 * Returns `null` for non-analytics iframe messages (theme / dimensions).
 */
export const toFrontendCheckoutEvent = (message: IframeMessage, eventData: EventData): CheckoutEventInput | null => {
  switch (message) {
    case IframeMessage.CHECKOUT_OPENED:
      return mapCheckoutLifecycleEvent('checkout_opened', eventData);
    case IframeMessage.ORDER_CREATED:
      return mapCheckoutLifecycleEvent('order_created', eventData);
    case IframeMessage.CLOSE_CHECKOUT:
      return mapCheckoutLifecycleEvent('checkout_closed', eventData);
    case IframeMessage.ORDER_COMPLETED_SUCCESS:
      return mapOrderAwardingEvent('success', eventData);
    case IframeMessage.ORDER_COMPLETED_FAILED:
      return mapOrderAwardingEvent('fail', eventData);
    case IframeMessage.ON_PAYMENT_INTENT:
      return mapPaymentIntentEvent('started', 'pending', eventData);
    case IframeMessage.PAYMENT_INTENT_SUCCESS:
      return mapPaymentIntentEvent('resolved', 'success', eventData);
    case IframeMessage.PAYMENT_INTENT_FAILED:
      return mapPaymentIntentEvent('resolved', 'fail', eventData);
    case IframeMessage.PAYMENT_METHOD_SELECTED:
      return mapPaymentMethodSelectedEvent(eventData);
    default:
      return null;
  }
};

export const mapCheckoutLifecycleEvent = (
  phase: CheckoutLifecyclePhase,
  eventData: EventData
): CheckoutLifecycleEventInput => {
  const event: CheckoutLifecycleEventInput = {
    event_type: FrontendCheckoutEventType.CHECKOUT_LIFECYCLE,
    phase,
    ...mapCoreFields(eventData),
  };

  if (eventData.paymentMethodName) {
    event.payment_method_name = eventData.paymentMethodName;
  }
  if (eventData.isSavedPaymentMethodUsed != null) {
    event.is_saved_payment_method_used = eventData.isSavedPaymentMethodUsed;
  }
  if (eventData.isNewPaymentMethodSaved != null) {
    event.is_new_payment_method_saved = eventData.isNewPaymentMethodSaved;
  }

  const error = mapLifecycleError(eventData);
  if (error) event.error = error;

  return event;
};

export const mapOrderAwardingEvent = (
  status: Extract<CheckoutPaymentStatus, 'success' | 'fail'>,
  eventData: EventData
): OrderAwardingEventInput => ({
  event_type: FrontendCheckoutEventType.ORDER_AWARDING,
  ...mapCoreFields(eventData),
  ...mapPaymentFields(eventData),
  status,
  reason: status === 'fail' ? mapFailureReason(eventData) : null,
});

export const mapPaymentIntentEvent = (
  stage: PaymentIntentStage,
  status: CheckoutPaymentStatus,
  eventData: EventData
): PaymentIntentEventInput => ({
  event_type: FrontendCheckoutEventType.PAYMENT_INTENT,
  ...mapCoreFields(eventData),
  ...mapPaymentFields(eventData),
  status,
  reason: status === 'fail' ? mapFailureReason(eventData) : null,
  stage,
});

/**
 * Builds a `CHECKOUT_UI_INTERACTIONS` event (`card_entry_started` /
 * `form_entry_completed`). Unlike the other checkout DTOs, these events have
 * no matching `IframeMessage` — they never `postMessage` to the publisher —
 * so callers build `eventData` themselves (e.g. via `getEventDataForSdkEvents`)
 * instead of going through `toFrontendCheckoutEvent`.
 */
export const mapCheckoutUiInteractionsEvent = (
  eventName: CardEntryEventName,
  cardEntry: CardEntryDetails,
  eventData: EventData,
  contextTags?: CheckoutUiInteractionsContextTags
): CardEntryUiInteractionsEventInput => {
  const event: CardEntryUiInteractionsEventInput = {
    event_type: FrontendCheckoutEventType.CHECKOUT_UI_INTERACTIONS,
    event_name: eventName,
    card_entry: cardEntry,
    ...mapCoreFields(eventData),
  };

  if (eventData.paymentMethodName) {
    event.payment_method_name = eventData.paymentMethodName;
  }
  if (contextTags?.checkoutPresentation) {
    event.checkout_presentation = contextTags.checkoutPresentation;
  }
  if (contextTags?.templateType) {
    event.template_type = contextTags.templateType;
  }

  return event;
};

export const mapPaymentMethodSelectedEvent = (eventData: EventData): PaymentMethodSelectedUiInteractionsEventInput => ({
  event_type: FrontendCheckoutEventType.CHECKOUT_UI_INTERACTIONS,
  event_name: 'payment_method_selected',
  ...mapCoreFields(eventData),
  payment_method_name: eventData.paymentMethodName ?? '',
  previous_payment_method_name: eventData.previousPaymentMethodName ?? '',
});

export const mapCoreFields = (eventData: EventData): CheckoutEventCoreFields => ({
  order_id: eventData.orderId ?? '',
  event_timestamp: mapEventTimestamp(eventData.date),
  bundle_id: eventData.bundleId ?? '',
  bundle_name: eventData.bundleName ?? '',
  bundle_sku: eventData.bundleSKU ?? '',
  products: mapProducts(eventData),
  session_id: eventData.sessionId ?? '',
  total_sum: eventData.totalSum ?? 0,
  total_sum_currency: eventData.totalSumCurrency ?? '',
  country_code: eventData.userCountry ?? '',
  player_id: eventData.userId ?? '',
  promo_code: mapPromoCode(eventData.promoCode),
});

export const mapProducts = (eventData: EventData): EventProduct[] =>
  (eventData.products ?? []).map((product) => ({
    name: product.name,
    sku: product.sku,
    quantity: String(product.quantity),
  }));

export const mapPaymentFields = (eventData: EventData) => ({
  payment_method_name: eventData.paymentMethodName ?? '',
  is_saved_payment_method_used: eventData.isSavedPaymentMethodUsed ?? false,
  is_new_payment_method_saved: eventData.isNewPaymentMethodSaved ?? false,
});

/** Maps FE camelCase promo → DTO snake_case `EventPromoCode`. */
export const mapPromoCode = (promoCode: PromoCodeData | null | undefined): EventPromoCode | null => {
  if (!promoCode) return null;
  return {
    name: promoCode.name,
    discount_amount: promoCode.discountAmount,
    discount_percentage: promoCode.discountPercentage,
  };
};

export const mapEventTimestamp = (date: string | undefined): string => {
  const epoch = Number(date);
  if (Number.isFinite(epoch) && epoch > 0) {
    return new Date(epoch).toISOString();
  }
  return new Date().toISOString();
};

export const mapFailureReason = (eventData: EventData): string | null =>
  eventData.reason ?? eventData.error?.reason ?? null;

export const mapLifecycleError = (eventData: EventData): CheckoutEventError | undefined => {
  if (!eventData.error && !eventData.reason) return undefined;
  return {
    ...(eventData.error?.isFatal != null ? { is_fatal: eventData.error.isFatal } : {}),
    ...(eventData.error?.reason || eventData.reason ? { reason: eventData.error?.reason ?? eventData.reason } : {}),
  };
};
